import { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { StrikeThroughHeader } from '../../components/Headers/strikethrough-header/strikethrough-header';
import { Box, Tab, Tabs, Alert } from '@mui/material';
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import ProducerDropdown from '../Software/Subpages/MyIssuedApps/components/ProducerDropdown';
import MyIMOTabPanel from './components/MyIMOTabPanel';
import StatusReports from './components/StatusReports';
import Contracting from './components/Contracting';
import PendingReports from './components/PendingReports';
import ChangeRequests from './components/ChangeRequests';
import Notifications from './Subpages/Notifications/Notifications';
import { Index as SupportStaff } from '../AgencyAdmin/Index/Index';
import SelectProducer from '../Reports/selectProducer/selectProducer';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const commonTabSx = {
  fontSize: { xs: '.7rem', md: 'default' },
  wrap: 'none',
  minWidth: { xs: 120, md: 160 },
};

const MyIMO = () => {
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, _setSearchParams] = useSearchParams();

  const hasProducerIDParam = searchParams.has('producerID');
  const producerIDParam = searchParams.get('producerID');

  const defaultProducerID = user.producerID?.split(',')[0] ?? '';
  const [producerID, setProducerID] = useState(
    hasProducerIDParam && producerIDParam !== 'null' ? producerIDParam : defaultProducerID
  );
  const [npnUpdateDetails, setNpnUpdateDetails] = useState(null);

  const [tab, setTab] = useState(0);
  const tabRoutes = [
    '/MyIMO/PendingReports',
    '/MyIMO/StatusReports',
    '/MyIMO/Contracting',
    '/MyIMO/ChangeRequests',
    '/MyIMO/SupportStaff',
    '/MyIMO/Notifications',
    '/MyIMO/MyProfile',
  ];

  const tabs = [
    'Pending Reports',
    'Status Reports',
    'Contracting',
    'Change Requests',
    'Support Staff',
    'Notifications',
    'My Profile',
  ];

  useEffect(() => {
    const tabIndex = tabRoutes.findIndex((path) => location.pathname.startsWith(path));
    if (tabIndex !== -1 && tabIndex !== tab) {
      setTab(tabIndex);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentSearch = location.search;

    if (location.pathname === '/MyIMO') {
      navigate(`${tabRoutes[0]}${currentSearch}`);
    } else {
      const matchedPath = tabRoutes.find((route) =>
        location.pathname.includes(route.split('/').pop())
      );
      if (matchedPath) {
        navigate(`${matchedPath}${currentSearch}`);
      }
    }
  }, []);

  const handleTabChange = (_event, newValue) => {
    const currentProducerID = searchParams.get('producerID') || defaultProducerID;
    setTab(newValue);
    navigate(`${tabRoutes[newValue]}?producerID=${currentProducerID}`);
  };

  function hasProducerID() {
    return !user.producerID ? false : true;
  }

  if (user.userType === 'Agent' && hasProducerID()) {
    return <Navigate to="/MyProfile/Demographic" />;
  }

  if (user.isHO === true) {
    return (
      <Box>
        <StrikeThroughHeader>My IMO</StrikeThroughHeader>
        <Alert sx={{ width: 'fit-content' }} severity="warning">
          No IMO Data Listed
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <StrikeThroughHeader>My IMO</StrikeThroughHeader>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Tabs
          value={tab > 5 ? false : tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable MyIMO tabs"
          sx={{
            width: { xs: '100%', md: 'auto' },
            maxWidth: '100%',
          }}
        >
          {tabs.map((label, index) => (
            <Tab key={index} sx={commonTabSx} label={label} {...a11yProps(index)} />
          ))}
        </Tabs>
        {tab === 4 ? (
          <SelectProducer
            setNpnUpdateDetails={setNpnUpdateDetails}
            containerSx={{ width: { xs: '100%', md: 'auto' } }}
            formControlSx={{
              width: { xs: '100%', md: 'auto' },
              margin: { xs: '1.5rem 0', md: 0 },
            }}
            variant="outlined"
            selectLabel="Producer IDs"
            showAbbrvProducer
            showLabel
          />
        ) : (
          <ProducerDropdown
            initialProducerID={producerID}
            defaultValue={producerID}
            formControlStyling={{
              width: { xs: '100%', md: 'auto' },
              margin: { xs: '1.5rem 0', md: 0 },
            }}
            setProducerID={(id) => {
              setProducerID(id);
            }}
            variant="outlined"
          />
        )}
      </Box>
      <MyIMOTabPanel value={tab} index={0}>
        {producerID && <PendingReports producerID={producerID} />}
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={1}>
        <StatusReports producerID={producerID} />
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={2}>
        <Contracting producerID={producerID} />
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={3}>
        <ChangeRequests producerID={producerID} />
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={4}>
        <SupportStaff
          npnID={
            npnUpdateDetails != null && npnUpdateDetails.length > 0
              ? npnUpdateDetails[0].newValue
              : ''
          }
        />
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={5}>
        <Notifications producerID={producerID} npnID={user.npnID} />
      </MyIMOTabPanel>
      <MyIMOTabPanel value={tab} index={6}>
        <Navigate to={`/MyProfile/Demographic/${producerID}`} />
      </MyIMOTabPanel>
    </>
  );
};

export default MyIMO;
